import { TrendingUp, TrendingDown, BarChart3, ChevronRight } from "lucide-react";
import type { LeaderboardEntry } from "@/hooks/useLeaderboard";
import { Tooltip, TooltipTrigger, TooltipContent } from "@/components/ui/tooltip";

interface FiltersIndexRowProps {
  entry: LeaderboardEntry;
  rank: number;
  influencerCount: number;
  onClick?: () => void;
}

const FiltersIndexRow = ({ entry, rank, influencerCount, onClick }: FiltersIndexRowProps) => {
  const netPnl = entry.netPnlCaptured;
  const score = entry.score;

  return (
    <button
      onClick={onClick}
      className="w-full text-left border-y-2 border-primary/30 bg-primary/5 hover:bg-primary/10 transition-all duration-300 cursor-pointer group"
    >
      {/* Desktop layout */}
      <div className="hidden md:grid grid-cols-[60px_minmax(160px,1fr)_100px_100px_100px_100px_110px_140px_40px] items-center gap-4 px-5 py-4">
        {/* Rank */}
        <div className="font-mono font-bold text-primary text-sm">
          #{rank}
        </div>

        {/* Identity */}
        <div className="flex items-center gap-3 min-w-0">
          <div className="w-10 h-10 rounded-full bg-primary/10 border border-primary/30 flex items-center justify-center shrink-0">
            <BarChart3 className="w-5 h-5 text-primary" />
          </div>
          <div className="min-w-0">
            <span className="font-semibold text-primary truncate flex items-center gap-1.5">
              Filters Index
            </span>
            <span className="font-mono text-xs text-muted-foreground">
              {influencerCount} influencers
            </span>
          </div>
        </div>

        {/* Score */}
        <Tooltip>
          <TooltipTrigger asChild>
            <div className={`font-mono font-bold text-sm text-center cursor-default ${score > 0 ? "text-profit" : score < 0 ? "text-loss" : "text-muted-foreground"}`}>
              {score > 0 ? "+" : ""}{score.toFixed(1)}
            </div>
          </TooltipTrigger>
          <TooltipContent side="top" className="max-w-xs">
            <p className="text-xs font-semibold mb-1">Filters Index Score</p>
            <p className="text-xs text-muted-foreground">Weighted aggregate of {influencerCount} influencers' positions</p>
          </TooltipContent>
        </Tooltip>

        {/* Avg PnL */}
        <div className={`font-mono font-bold text-sm flex items-center justify-center gap-1 ${entry.avgPnl > 0 ? "text-profit" : entry.avgPnl < 0 ? "text-loss" : "text-muted-foreground"}`}>
          {entry.avgPnl > 0 ? <TrendingUp className="w-3 h-3" /> : entry.avgPnl < 0 ? <TrendingDown className="w-3 h-3" /> : null}
          {entry.avgPnl > 0 ? "+" : ""}{entry.avgPnl.toFixed(1)}%
        </div>

        {/* PnL/day */}
        <div className="font-mono font-bold text-sm text-center text-foreground">
          {entry.avgPnlPerDay > 0 ? "+" : ""}{entry.avgPnlPerDay.toFixed(2)}%
        </div>

        {/* Win Rate */}
        <div className={`font-mono font-bold text-sm text-center ${entry.winRate >= 50 ? "text-profit" : "text-loss"}`}>
          {entry.winRate}%
        </div>

        {/* Net PnL */}
        <div className={`font-mono font-bold text-sm text-center ${netPnl > 0 ? "text-profit" : netPnl < 0 ? "text-loss" : "text-muted-foreground"}`}>
          {netPnl > 0 ? "+" : ""}{netPnl.toFixed(1)}%
        </div>

        {/* Calls / Positions */}
        <div className="font-mono text-sm text-foreground text-center">
          {entry.totalActionableCalls} / {entry.totalCalls}
        </div>

        {/* Arrow */}
        <ChevronRight className="w-4 h-4 text-muted-foreground group-hover:text-primary transition-colors" />
      </div>

      {/* Mobile layout */}
      <div className="md:hidden px-4 py-3">
        <div className="flex items-center gap-2">
          <div className="font-mono font-bold text-primary text-xs w-7 shrink-0 text-center">
            #{rank}
          </div>
          <div className="w-8 h-8 rounded-full bg-primary/10 border border-primary/30 flex items-center justify-center shrink-0">
            <BarChart3 className="w-4 h-4 text-primary" />
          </div>
          <div className="flex-1 min-w-0 overflow-hidden">
            <span className="font-semibold text-primary text-sm truncate block">
              Filters Index
            </span>
            <span className="font-mono text-[11px] text-muted-foreground block">
              {influencerCount} influencers
            </span>
          </div>
          <div className={`font-mono font-bold text-sm shrink-0 ${entry.avgPnl > 0 ? "text-profit" : entry.avgPnl < 0 ? "text-loss" : "text-muted-foreground"}`}>
            {entry.avgPnl > 0 ? "+" : ""}{entry.avgPnl.toFixed(1)}%
          </div>
          <ChevronRight className="w-4 h-4 text-muted-foreground shrink-0" />
        </div>
        <div className="flex items-center gap-3 mt-1.5 ml-[calc(1.75rem+0.5rem+2rem+0.5rem)] text-[11px] font-mono text-muted-foreground">
          <span className={score > 0 ? "text-profit" : score < 0 ? "text-loss" : ""}>
            Score: {score > 0 ? "+" : ""}{score.toFixed(1)}
          </span>
          <span className={entry.winRate >= 50 ? "text-profit" : "text-loss"}>
            {entry.winRate}% win
          </span>
          <span>{entry.totalActionableCalls}/{entry.totalCalls} calls</span>
        </div>
      </div>
    </button>
  );
};

export default FiltersIndexRow;
